import React, { useEffect, useRef, useState } from "react";
import { StrokedText } from "../ui/text/stroked-text";
import { S3_BASE_URL } from "@/utils/s3";
import cn from "classnames";

export interface KillEvent {
  id: string;
  killerName: string;
  victimName: string;
  killerTeam: "A" | "B";
  victimTeam: "A" | "B";
  killerBaseName: string;
  victimBaseName: string;
}

interface KillAnnouncerProps {
  events: KillEvent[];
}

export const KillAnnouncer: React.FC<KillAnnouncerProps> = ({ events }) => {
  const [activeEvents, setActiveEvents] = useState<KillEvent[]>([]);
  const announcedEventIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    // If events array is cleared (e.g. restart), clear our history
    if (events.length === 0) {
      announcedEventIds.current.clear();
      setActiveEvents([]);
      return;
    }

    if (events.length > 0) {
      // Only process events we haven't announced yet
      const newEvents = events.filter(
        (e) => !announcedEventIds.current.has(e.id),
      );

      if (newEvents.length > 0) {
        // Mark as announced immediately
        newEvents.forEach((e) => announcedEventIds.current.add(e.id));

        setActiveEvents((prev) => [...prev, ...newEvents]);

        // Schedule removal for each new event
        newEvents.forEach((event) => {
          setTimeout(() => {
            setActiveEvents((prev) => prev.filter((e) => e.id !== event.id));
          }, 3500); // Show for 3.5 seconds
        });
      }
    }
  }, [events]);

  return (
    <div className="absolute top-[12%] left-0 w-full flex flex-col items-center pointer-events-none z-[9999] gap-2">
      {activeEvents.map((event) => (
        <div
          key={event.id}
          className="animate-in fade-in zoom-in-90 slide-in-from-top-4 duration-300 flex items-center justify-center"
        >
          <div className="relative">
            {/* Background Glow/Banner - Smaller height */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/80 to-transparent blur-md transform scale-105" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-red-900/40 to-transparent blur-sm" />

            {/* Content - Compact padding */}
            <div className="relative px-8 py-2 flex items-center gap-3 bg-gradient-to-r from-transparent via-black/70 to-transparent border-y border-white/10">
              {/* Killer Portrait */}
              <div
                className={cn(
                  "w-10 h-10 rounded-full border-2 overflow-hidden bg-gray-800",
                  event.killerTeam === "A"
                    ? "border-blue-400"
                    : "border-red-400",
                )}
              >
                <img
                  src={`${S3_BASE_URL}/pokemons/${event.killerBaseName}/portraits/Normal.png`}
                  alt={event.killerName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>

              {/* Killer Name */}
              <div className="flex flex-col items-end">
                <StrokedText
                  text={event.killerName}
                  className={cn(
                    "text-lg font-bold font-vck uppercase",
                    event.killerTeam === "A" ? "text-blue-400" : "text-red-400",
                  )}
                  strokeWidth={3}
                  strokeColor="black"
                  fontSize={14}
                />
              </div>

              {/* Sword Icon SVG - Smaller */}
              <div className="text-yellow-500 animate-pulse mx-1">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="drop-shadow-lg"
                >
                  <path
                    d="M14.5 9.5L9.5 14.5M6.5 17.5L3 21M21 3L17.5 6.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14.5 3.5L20.5 9.5L12 18L5 11L14.5 3.5Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 16L6 20"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {/* Victim Name */}
              <div className="flex flex-col items-start">
                <StrokedText
                  text={event.victimName}
                  className={cn(
                    "text-lg font-bold font-vck uppercase",
                    event.victimTeam === "A" ? "text-blue-400" : "text-red-400",
                  )}
                  strokeWidth={3}
                  strokeColor="black"
                  fontSize={14}
                />
              </div>

              {/* Victim Portrait */}
              <div
                className={cn(
                  "w-10 h-10 rounded-full border-2 overflow-hidden bg-gray-800",
                  event.victimTeam === "A"
                    ? "border-blue-400"
                    : "border-red-400",
                )}
              >
                <img
                  src={`${S3_BASE_URL}/pokemons/${event.victimBaseName}/portraits/Hurt.png`}
                  onError={(e) => {
                    // Fallback to Normal if Hurt doesn't exist
                    const target = e.target as HTMLImageElement;
                    const fallback = `${S3_BASE_URL}/pokemons/${event.victimBaseName}/portraits/Normal.png`;
                    if (target.src !== fallback) {
                      target.src = fallback;
                    }
                  }}
                  alt={event.victimName}
                  className="w-full h-full object-cover grayscale"
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
